import React from 'react'
import { makeStyles, Theme } from '@material-ui/core/styles'
import { Formik, Field } from 'formik'
import LiquidInputAdapter from '@src/components/LiquidInputAdapter'
import { Button } from '@material-ui/core'
import { FormattedMessage, useIntl } from 'react-intl'
import messages from '../../../messages'
import { ProductDetails, configContext } from '../Sirna'
import { useGetSearchSiRnaLazyQuery } from '@src/queries/PredesignedSiRNAQuery.generated'
import { SiRNAProperties } from '../enum'
import ErrorAlert from '../../../../Configurators/components/ErrorAlert'
import ValidationErrors from '../../../../Configurators/validations/ValidationErrors'
import { validateText } from '@src/routes/SemiConfigurators/utils/util'
import { useRouter } from '@src/routes'
import CircleLoader from '@src/components/CircleLoader/CircleLoader'
import { initialState, SiRNAData } from '../Reducer'
import { SemiConfiguratorValues } from '../../../utils/constants'
import styles from '@src/routes/SemiConfigurators/utils/styles'
import { dropAllKeyInstances } from '@src/utils/ObjectHelpers/dropKeys'

const useStyles = makeStyles((theme: Theme) => ({
  text: {
    color: theme.palette.common.black,
    fontSize: theme.typography.pxToRem(14),
    margin: theme.spacing(2, 0, 2, 0),
  },
  textarea: {
    backgroundColor: theme.palette.background.grey,
    overflow: 'auto',
    fontFamily: 'inherit',
    margin: theme.spacing(1, 25, 0, 0),
    borderRadius: 5,
    resize: 'none',
    height: theme.spacing(25),
  },
  margin: {
    margin: theme.spacing(4, 0, 2, 0),
  },
  marginLeft: {
    margin: theme.spacing(0, 2, 0, 0),
  },
  contentWraper: {
    backgroundColor: theme.palette.common.white,
    padding: theme.spacing(4),
  },
  chip: {
    padding: theme.spacing(2, 0, 0, 2),
  },
  searchButton: {
    margin: theme.spacing(4, 0, 0, 0),
  },
  textAreaSize: {
    height: '53px',
    paddingRight: theme.spacing(21.5),
  },
  heading: {
    fontSize: theme.typography.pxToRem(20),
  },
}))

interface GenesSearchProps {
  productDetails: ProductDetails
  saveSessionHandler: any
}

const GenesSearch: React.FC<GenesSearchProps> = (props) => {
  const classes = useStyles()
  const globalClasses = styles()
  const { productDetails, saveSessionHandler } = props
  const searchHeading = <FormattedMessage {...messages.CONF_SEARCH_GENES} />
  const { state, dispatch } = React.useContext(configContext)
  const {
    searchInput,
    searchResult,
    searchError,
    checkboxUpdate,
    geneSearchError,
    selectedData,
    perPageCount,
  } = state
  const [getSearchSiRNA, { data, loading }] = useGetSearchSiRnaLazyQuery({
    fetchPolicy: 'network-only',
  })
  const [openError, setOpenError] = React.useState<boolean>(false)
  const intl = useIntl()
  const [validationMessage, setValidationMessage] = React.useState<string[]>([])
  const value = { ...state.searchInput }
  const router = useRouter()
  const searchText = (
    <FormattedMessage {...messages.SEMICONF_SEARCH_KICQSTART} />
  )

  const FetchSearchData = async (obj): Promise<void> => {
    await getSearchSiRNA({
      variables: {
        input: { ...obj },
      },
    })
    dispatch({
      type: 'SEARCH_INPUT',
      data: obj,
    })
  }

  const getTermValue = (event, keys): void => {
    dispatch({
      type: 'UPDATE_SEARCH_ERROR',
      data: false,
    })
    dispatch({ type: 'GENE_SEARCH_ERROR', data: true })
    dispatch({ type: 'SET_PAGE_ERROR', data: '' })
    dispatch({ type: 'IS_CHIP_DELETE', data: false })
    setValidationMessage([''])
    dispatch({
      type: 'UPDATE_SEARCH_RESULT',
      data: initialState.searchResult,
    })
    if (state.stepNo) {
      dispatch({ type: 'TRACE_ON_CLICK', data: true })
    }
    if (!keys.length) {
      let ret = validateText(event.target.value)
      if (event.target.value.endsWith('  ')) {
        ret = event.target.value.replace('  ', ' ')
      }
      dispatch({
        type: 'SEARCH_INPUT',
        data: { ...state.searchInput, term: ret },
      })
    }
  }

  const handleSearch = (): void => {
    dispatch({ type: 'CHECKBOX_UPDATE', data: false })
    dispatch({ type: 'IS_CHIP_DELETE', data: false })
    dispatch({
      type: 'UPDATE_SELECTED_DATA',
      data: [],
    })
    dispatch({
      type: 'UPDATE_DATA',
      data: [],
    })
    if (!state.stepNo) {
      dispatch({ type: 'TRACE_ON_CLICK', data: false })
    } else if (state.stepNo) {
      dispatch({ type: 'TRACE_ON_CLICK', data: true })
    }
    const terms = state.searchInput.term.trim()
    const chips = terms.split(/[\s, ]+/)
    if (!state.searchInput.term) {
      dispatch({
        type: 'SET_PAGE_ERROR',
        data: intl.formatMessage(messages.CONF_DNAPLATES_ERROR_STEP2),
      })
      setValidationMessage([intl.formatMessage(messages.CONF_REQUIRED_FIELD)])
      dispatch({ type: 'GENE_SEARCH_ERROR', data: true })
      setOpenError(true)
    } else if (chips.length > 25) {
      dispatch({
        type: 'SET_PAGE_ERROR',
        data: intl.formatMessage(messages.CONF_DNAPLATES_ERROR_STEP2),
      })
      setValidationMessage([
        intl.formatMessage(messages.SEMICONF_UPLOAD_ERROR_MSG_sirna),
      ])
      window.scrollTo(0, 0)
      dispatch({ type: 'GENE_SEARCH_ERROR', data: true })
    }
    const updateCountryCode = {
      ...state.searchInput,
      locale: productDetails.locale,
      region: productDetails.region,
      facet: initialState.searchInput.facet,
      count: 10,
      accessToken: productDetails.participantId,
      store: productDetails.store,
    }
    dispatch({
      type: 'SET_PER_PAGE_COUNT',
      data: initialState.perPageCount,
    })
    //remove term param from the URL
    const { pathname, query } = router
    if (query?.term) {
      delete query?.term
      router.push({ pathname, query }, undefined, { shallow: true })
    }
    if (chips.length <= 25 && state.searchInput.term) {
      FetchSearchData(updateCountryCode)
    } else {
      saveSessionHandler({
        results: initialState.searchResult.results,
        term: chips.length <= 25 ? chips : '',
        count: 0,
        pageNo: 1,
        stepNo: 0,
        perPage: perPageCount,
        selectedItems: [],
      })
    }
  }

  React.useEffect(() => {
    window.scrollTo(0, 0)
    dispatch({ type: 'GENE_SEARCH_ERROR', data: false })
    dispatch({ type: 'SET_PAGE_ERROR', data: '' })
    dispatch({ type: 'IS_CHIP_DELETE', data: false })
    if (router !== null && router.query !== null) {
      let term = ''
      if (productDetails.countryCode !== SiRNAProperties.China) {
        term = router.query.term || ''
        if (term) {
          const updateQueryTerm = {
            ...state.searchInput,
            term,
            locale: productDetails.locale,
            region: productDetails.region,
            accessToken: productDetails.participantId,
            store: productDetails.store,
          }
          FetchSearchData(updateQueryTerm)
        }
      }
    }

    // TODO: need a test before we refactor these dependencies
  }, [router.query.term])

  React.useEffect(() => {
    if (data) {
      const { getSearchSiRNA } = data
      const siRNASearchData: SiRNAData[] = []
      if (getSearchSiRNA) {
        if (!getSearchSiRNA?.serverError) {
          const { results, term, count } = getSearchSiRNA
          if (count !== 0 && results) {
            setOpenError(false)
            results.forEach((data) => {
              const siRNASearchDataSingle: any = {}
              const siRNASearchSubData: any = []

              if (data) {
                const dataRecast = dropAllKeyInstances(data, ['__typename'])
                data = dataRecast
              }

              if (data) {
                siRNASearchDataSingle.refseqId = data.refseqId
                siRNASearchDataSingle.species = data.species
                siRNASearchDataSingle.siRNAType = data.siRNAType
                if (data.subSetInfo) {
                  data.subSetInfo.forEach((item) => {
                    if (item) {
                      const subSetInfo = {
                        ...item,
                        count: count || 0,
                        checked: false,
                        purification: SiRNAProperties.P1007.toString(),
                        size: SiRNAProperties.S1030.toString(),
                      }

                      //check whether item is selected
                      const sequence: SiRNAData | undefined =
                        selectedData?.find(
                          (seq) => seq?.refseqId === subSetInfo.refseqId
                        )
                      if (sequence) {
                        const checkedItem = sequence.subSetInfo?.find(
                          (seqItem) => seqItem.siRNAId === subSetInfo.siRNAId
                        )
                        if (checkedItem) {
                          subSetInfo.checked = true
                          subSetInfo.purification = checkedItem.purification
                          subSetInfo.size = checkedItem.size
                        }
                      }
                      siRNASearchSubData.push(subSetInfo)
                    }
                  })
                }

                siRNASearchDataSingle.subSetInfo = siRNASearchSubData
                siRNASearchData.push(siRNASearchDataSingle)
              }
            })
            dispatch({ type: 'GENE_SEARCH_ERROR', data: false })
            dispatch({
              type: 'UPDATE_DATA',
              data: siRNASearchData,
            })
          } else if (!checkboxUpdate) {
            dispatch({
              type: 'SET_PAGE_ERROR',
              data: intl.formatMessage(messages.CONF_DNAPLATES_ERROR_STEP2),
            })
            setValidationMessage([
              `${intl.formatMessage(
                messages.CONF_MANUAL_SEARCH_1_ERROR_POPUP_crispgrna
              )}${value.term}${intl.formatMessage(
                messages.CONF_MANUAL_SEARCH_2_ERROR_POPUP_crispgrna
              )}`,
            ])
            dispatch({ type: 'GENE_SEARCH_ERROR', data: true })
          }
          dispatch({
            type: 'UPDATE_SEARCH_RESULT',
            data: {
              ...searchResult,
              results: siRNASearchData,
              term: term,
              count: count,
            },
          })
          saveSessionHandler({
            results: siRNASearchData,
            term: count && count > 0 ? term : '',
            count: count,
            pageNo: 1,
            stepNo: 0,
            perPage: perPageCount,
            selectedItems: selectedData || [],
          })
          if (count) {
            dispatch({
              type: 'UPDATE_PAGINATION_DATA',
              data: {
                ...state.paginationData,
                totalCount: count,
              },
            })
          }
        } else {
          dispatch({
            type: 'SET_PAGE_ERROR',
            data: SemiConfiguratorValues.InternalServerError,
          })
          window.scrollTo(0, 0)
        }
      }
    }
    // TODO: need a test before we refactor these dependencies
  }, [data])

  React.useEffect(() => {
    if (
      (state.stepNo === 1 &&
        state.traceOnClick &&
        searchInput.term.length &&
        !geneSearchError) ||
      (state.stepNo === 0 && searchInput.term.length && !geneSearchError) ||
      state.traceOnClick ||
      state.isChipDelete ||
      (state.stepNo === 1 &&
        state.traceOnClick &&
        !checkboxUpdate &&
        !geneSearchError)
    ) {
      FetchSearchData(searchInput)
      if (state.isChipDelete) dispatch({ type: 'IS_CHIP_DELETE', data: false })
    }
    // TODO: need a test before we refactor these dependencies
  }, [
    searchInput.facet,
    searchInput.page,
    searchInput.count,
    state.isChipDelete,
  ])

  React.useEffect(() => {
    if (searchError) {
      dispatch({
        type: 'SET_PAGE_ERROR',
        data: intl.formatMessage(messages.CONF_SELECT_ASSAY_ERROR_MSG),
      })
      setOpenError(true)
    } else {
      setOpenError(false)
    }
    dispatch({ type: 'GENE_SEARCH_ERROR', data: false })
    // TODO: need a test before we refactor these dependencies
  }, [searchError])

  React.useEffect(() => {
    if (!state.pageError.length) {
      setOpenError(false)
    } else {
      setOpenError(true)
    }
  }, [state.pageError])

  React.useEffect(() => {
    if (searchError) {
      dispatch({
        type: 'SET_PAGE_ERROR',
        data: intl.formatMessage(messages.CONF_SELECT_ASSAY_ERROR_MSG),
      })
      setOpenError(true)
    } else if (!state.pageError.length) {
      setOpenError(false)
    }
    // TODO: need a test before we refactor these dependencies
  }, [selectedData])

  return (
    <Formik
      initialValues={{ SiRNA: '' }}
      onSubmit={(values, actions) => {
        setTimeout(() => {
          actions.setSubmitting(false)
        }, 1000)
      }}
    >
      <>
        <ErrorAlert
          open={openError}
          setOpen={setOpenError}
          message={state.pageError}
        />
        <div className={classes.contentWraper}>
          <h2 className={classes.heading}> {searchHeading}</h2>
          <p className={classes.text}>{searchText}</p>
          <Field
            name="addInfo"
            component={LiquidInputAdapter}
            multiline
            rows={4}
            rowsMax={4}
            className={globalClasses.manualSearchText}
            filled
            value={state.searchInput.term}
            onChange={(e) => getTermValue(e, '')}
          />
          {geneSearchError && (
            <div>
              <ValidationErrors errors={validationMessage} />
            </div>
          )}
          <Button
            size="large"
            variant="contained"
            color="primary"
            className={classes.searchButton}
            onClick={handleSearch}
          >
            <FormattedMessage {...messages.order_field_label_search_orders} />
          </Button>
        </div>
        {loading && <CircleLoader />}
      </>
    </Formik>
  )
}

export default GenesSearch
